import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import SwiftAlert from "../../components/SwiftAlert";
import { queryCache } from "../../lib/queryCache";
import {
  createStudyGroup,
  deleteStudyGroup,
  generateStudyGroupCode,
  getStudyGroupMembers,
  getStudyGroups,
  joinStudyGroupByCode,
  leaveStudyGroup,
  removeStudyGroupMember,
  removeStudyGroupMembersBatch,
  updateStudyGroup,
  getStudyGroupRequests,
  acceptStudyGroupRequest,
  declineStudyGroupRequest,
  inviteStudyGroupMember,
  searchUserByIdentifier,
} from "../../services/api";
import Swal from "sweetalert2";

import StudyGroupHero from "../../components/StudyGroup/StudyGroupHero";
import StudyGroupCard from "../../components/StudyGroup/StudyGroupCard";
import CreateGroupModal from "../../components/StudyGroup/modals/CreateGroupModal";
import EditGroupModal from "../../components/StudyGroup/modals/EditGroupModal";
import EnterCodeModal from "../../components/StudyGroup/modals/EnterCodeModal";
import ManageGroupModal from "../../components/StudyGroup/modals/ManageGroupModal";
import ConfirmActionModal from "../../components/StudyGroup/modals/ConfirmActionModal";
import RemoveMemberModal from "../../components/StudyGroup/modals/RemoveMemberModal";
import StudyBuddiesModal from "../../components/StudyGroup/modals/StudyBuddiesModal";

const StudyGroup = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Data states (cached)
  const [groups, setGroupsState] = useState(() => queryCache.get("study_groups_mapped") || []);
  const [isLoadingGroups, setIsLoadingGroups] = useState(() => !queryCache.get("study_groups_mapped"));

  const setGroups = (updater) => {
    setGroupsState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      queryCache.set("study_groups_mapped", next, 5 * 60 * 1000);
      return next;
    });
  };

  // Read search from URL query parameter (?q=)
  const urlQuery = useMemo(() => {
    return new URLSearchParams(location.search).get("q") || "";
  }, [location.search]);

  // Search, Filter & Sort states
  const [searchQuery, setSearchQuery] = useState(urlQuery);
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'owned' | 'joined'
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [sortBy, setSortBy] = useState("recent"); // 'recent' | 'name' | 'members'

  // Sync searchQuery when URL query parameter updates (e.g. from Header search bar)
  useEffect(() => {
    setSearchQuery(urlQuery);
  }, [urlQuery]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    const params = new URLSearchParams(location.search);
    const trimmed = value.trim();
    if (trimmed) {
      params.set("q", trimmed);
    } else {
      params.delete("q");
    }
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
  };

  const handleClearSearch = () => {
    handleSearchChange("");
  };

  // Toast alert
  const [alert, setAlert] = useState(null);

  // Modals & Action states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEnterCodeModalOpen, setIsEnterCodeModalOpen] = useState(false);
  const [isStudyBuddiesModalOpen, setIsStudyBuddiesModalOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [inviteCode, setInviteCode] = useState("");

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: "",
    groupId: null,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    isProcessing: false,
  });

  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [manageGroup, setManageGroup] = useState(null);
  const manageGroupRef = useRef(manageGroup);
  useEffect(() => {
    manageGroupRef.current = manageGroup;
  }, [manageGroup]);
  const [manageTab, setManageTab] = useState("members"); // 'members' | 'requests' | 'invite'
  const [manageMembers, setManageMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isRemovingMemberId, setIsRemovingMemberId] = useState("");
  const [removeMemberModal, setRemoveMemberModal] = useState({ open: false, member: null });
  const [manageInviteCode, setManageInviteCode] = useState("");
  const [isGeneratingManageCode, setIsGeneratingManageCode] = useState(false);
  const [joinRequests, setJoinRequests] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);

  // Direct Invite states
  const [directInviteQuery, setDirectInviteQuery] = useState("");
  const [directInviteResults, setDirectInviteResults] = useState([]);
  const [isSearchingDirect, setIsSearchingDirect] = useState(false);
  const [directInviteError, setDirectInviteError] = useState("");
  const [invitingUserId, setInvitingUserId] = useState("");

  // Form states
  const [groupData, setGroupData] = useState({
    name: "",
    subject: "",
    maxMembers: 5,
    color: "blue",
  });
  const [editGroupData, setEditGroupData] = useState({
    name: "",
    subject: "",
    maxMembers: 5,
    color: "blue",
  });
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [isJoiningCode, setIsJoiningCode] = useState(false);

  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [avatarFallbackMap, setAvatarFallbackMap] = useState({});

  const mapApiGroup = (group) => ({
    id: group?._id,
    name: group?.name || "Untitled Group",
    subject: group?.subject || "Uncategorized",
    members: Number(group?.members || 0),
    maxMembers: Number(group?.maxMembers || 5),
    schedule: new Date(group?.createdAt || Date.now()).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    color: group?.color || "blue",
    isShared: Boolean(group?.isShared),
    inviteCode: group?.inviteCode || "",
    inviteCodeActive: Boolean(group?.inviteCodeActive),
    pendingRequestsCount: Number(group?.pendingRequestsCount || 0),
    unreadMessagesCount: Number(group?.unreadMessagesCount || 0),
    hasUnreadMessages: Boolean(group?.hasUnreadMessages || Number(group?.unreadMessagesCount || 0) > 0),
    createdAt: group?.createdAt || Date.now(),
    updatedAt: group?.updatedAt || Date.now(),
  });

  const loadGroups = async (force = false) => {
    const cached = queryCache.get("study_groups_mapped");
    if (cached && Array.isArray(cached) && cached.length > 0) {
      setGroupsState(cached);
      setIsLoadingGroups(false);
    } else {
      setIsLoadingGroups(true);
    }

    try {
      const data = await getStudyGroups();
      setGroups(Array.isArray(data) ? data.map(mapApiGroup) : []);
    } catch (error) {
      if (!cached || !Array.isArray(cached) || cached.length === 0) {
        setAlert({
          type: "error",
          title: "Failed to Load Groups",
          message: error?.response?.data?.message || "Unable to fetch study groups right now.",
        });
      }
    } finally {
      setIsLoadingGroups(false);
    }
  };

  useEffect(() => {
    loadGroups(true);

    const handleGroupUpdated = () => {
      loadGroups(true);
    };

    const handleRequestsChanged = (e) => {
      const changedGroupId = e?.detail?.groupId ? String(e.detail.groupId) : null;
      const count = Number(e?.detail?.pendingRequestsCount ?? 0);

      if (changedGroupId) {
        setGroups((prev) =>
          prev.map((g) =>
            String(g.id) === changedGroupId
              ? { ...g, pendingRequestsCount: count }
              : g
          )
        );

        if (manageGroupRef.current && String(manageGroupRef.current.id) === changedGroupId) {
          getStudyGroupRequests(changedGroupId)
            .then((reqs) => setJoinRequests(Array.isArray(reqs) ? reqs : []))
            .catch(() => {});
        }
      } else {
        loadGroups(true);
      }
    };

    const handleMessageReceived = (e) => {
      const gId = e?.detail?.groupId ? String(e.detail.groupId) : null;
      if (gId) {
        setGroups((prev) =>
          prev.map((g) =>
            String(g.id) === gId
              ? {
                  ...g,
                  unreadMessagesCount: Number(g.unreadMessagesCount || 0) + 1,
                  hasUnreadMessages: true,
                }
              : g
          )
        );
      }
    };

    const handleChatRead = (e) => {
      const gId = e?.detail?.groupId ? String(e.detail.groupId) : null;
      if (gId) {
        setGroups((prev) =>
          prev.map((g) =>
            String(g.id) === gId
              ? { ...g, unreadMessagesCount: 0, hasUnreadMessages: false }
              : g
          )
        );
      }
    };

    window.addEventListener("study_group_updated", handleGroupUpdated);
    window.addEventListener("study_group_requests_changed", handleRequestsChanged);
    window.addEventListener("study_group_message_received", handleMessageReceived);
    window.addEventListener("study_group_chat_read", handleChatRead);
    return () => {
      window.removeEventListener("study_group_updated", handleGroupUpdated);
      window.removeEventListener("study_group_requests_changed", handleRequestsChanged);
      window.removeEventListener("study_group_message_received", handleMessageReceived);
      window.removeEventListener("study_group_chat_read", handleChatRead);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const onClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Compute live statistics
  const totals = useMemo(() => {
    const totalGroups = groups.length;
    const ownedCount = groups.filter((g) => !g.isShared).length;
    const joinedCount = groups.filter((g) => g.isShared).length;
    const totalMembers = groups.reduce((sum, g) => sum + g.members, 0);
    const totalPendingRequests = groups.reduce(
      (sum, g) => (!g.isShared ? sum + Number(g.pendingRequestsCount || 0) : sum),
      0
    );
    return { totalGroups, ownedCount, joinedCount, totalMembers, totalPendingRequests };
  }, [groups]);

  // Extract unique subjects
  const availableSubjects = useMemo(() => {
    const subs = new Set();
    groups.forEach((g) => {
      if (g.subject && g.subject.trim()) {
        subs.add(g.subject.trim());
      }
    });
    return Array.from(subs).sort();
  }, [groups]);

  // Filter and sort groups
  const filteredGroups = useMemo(() => {
    let result = [...groups];

    // Tab filter
    if (activeTab === "owned") {
      result = result.filter((g) => !g.isShared);
    } else if (activeTab === "joined") {
      result = result.filter((g) => g.isShared);
    }

    // Subject filter
    if (selectedSubject !== "all") {
      result = result.filter(
        (g) => (g.subject || "").toLowerCase() === selectedSubject.toLowerCase()
      );
    }

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (g) =>
          (g.name || "").toLowerCase().includes(q) ||
          (g.subject || "").toLowerCase().includes(q) ||
          (g.inviteCode || "").toLowerCase().includes(q)
      );
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === "name") {
        return a.name.localeCompare(b.name);
      }
      if (sortBy === "members") {
        return b.members - a.members;
      }
      // default: recent
      return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
    });

    return result;
  }, [groups, activeTab, selectedSubject, searchQuery, sortBy]);

  // --- Handlers ---
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!groupData.name.trim() || isSubmittingCreate) return;

    try {
      setIsSubmittingCreate(true);
      const created = await createStudyGroup({
        name: groupData.name.trim(),
        subject: groupData.subject.trim() || "Uncategorized",
        maxMembers: Number(groupData.maxMembers) || 5,
        color: groupData.color,
      });

      if (created) {
        setGroups((prev) => [mapApiGroup(created), ...prev]);
      }
      setGroupData({ name: "", subject: "", maxMembers: 5, color: "blue" });
      setIsCreateModalOpen(false);
      setAlert({
        type: "success",
        title: "Study Group Created",
        message: `"${created?.name || groupData.name}" is now ready for collaboration!`,
      });
    } catch (error) {
      setAlert({
        type: "error",
        title: "Creation Failed",
        message: error?.response?.data?.message || "Unable to create group.",
      });
    } finally {
      setIsSubmittingCreate(false);
    }
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    if (!editGroupData.name.trim() || !editingGroupId || isSubmittingEdit) return;

    try {
      setIsSubmittingEdit(true);
      const updated = await updateStudyGroup(editingGroupId, {
        name: editGroupData.name.trim(),
        subject: editGroupData.subject.trim() || "Uncategorized",
        maxMembers: Number(editGroupData.maxMembers) || 5,
        color: editGroupData.color,
      });

      if (updated) {
        setGroups((prev) =>
          prev.map((g) => (g.id === editingGroupId ? mapApiGroup(updated) : g))
        );
      }
      setIsEditModalOpen(false);
      setEditingGroupId(null);
      setAlert({
        type: "success",
        title: "Group Updated",
        message: "Study group settings have been saved.",
      });
    } catch (error) {
      setAlert({
        type: "error",
        title: "Update Failed",
        message: error?.response?.data?.message || "Unable to update group.",
      });
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleJoinWithCode = async () => {
    const cleanCode = inviteCode.trim().toUpperCase();
    if (!cleanCode) {
      setAlert({
        type: "error",
        title: "Invalid Code",
        message: "Please enter a valid study group invite code.",
      });
      return;
    }

    try {
      setIsJoiningCode(true);
      const joined = await joinStudyGroupByCode(cleanCode);
      if (joined) {
        setGroups((prev) => {
          const mapped = mapApiGroup(joined);
          const exists = prev.some((g) => g.id === mapped.id);
          return exists ? prev : [mapped, ...prev];
        });
      }
      setIsEnterCodeModalOpen(false);
      setInviteCode("");
      setAlert({
        type: "success",
        title: "Join Request Sent",
        message: "Your request to join the group has been sent. Waiting for the group admin to approve.",
      });
    } catch (error) {
      setAlert({
        type: "error",
        title: "Join Failed",
        message: error?.response?.data?.message || "Unable to join group with this code.",
      });
    } finally {
      setIsJoiningCode(false);
    }
  };

  const handlePasteCode = async () => {
    try {
      if (navigator?.clipboard?.readText) {
        const text = await navigator.clipboard.readText();
        if (text) setInviteCode(text.trim().toUpperCase());
      }
    } catch {
      // no-op
    }
  };

  const handleManageGroup = async (group) => {
    try {
      setManageGroup(group);
      setManageTab("members");
      setIsManageModalOpen(true);
      setIsLoadingMembers(true);
      setIsLoadingRequests(true);
      setOpenMenuId(null);
      setDirectInviteQuery("");
      setDirectInviteResults([]);
      setDirectInviteError("");

      const data = await getStudyGroupMembers(group.id);
      setManageMembers(Array.isArray(data?.members) ? data.members : []);
      setManageInviteCode(group?.inviteCode || "");

      try {
        const reqs = await getStudyGroupRequests(group.id);
        const reqList = Array.isArray(reqs) ? reqs : [];
        setJoinRequests(reqList);
        setGroups((prev) =>
          prev.map((g) =>
            g.id === group.id ? { ...g, pendingRequestsCount: reqList.length } : g
          )
        );
      } catch (reqErr) {
        console.error("Failed to fetch requests", reqErr);
      }
    } catch (error) {
      setIsManageModalOpen(false);
      setAlert({
        type: "error",
        title: "Manage Group Unavailable",
        message: error?.response?.data?.message || "Unable to load group details.",
      });
    } finally {
      setIsLoadingMembers(false);
      setIsLoadingRequests(false);
    }
  };

  const handleSearchDirectUser = async (e) => {
    if (e) e.preventDefault();
    const q = directInviteQuery.trim();
    if (q.length < 2) {
      setDirectInviteError("Enter at least 2 characters.");
      return;
    }
    setDirectInviteError("");
    setIsSearchingDirect(true);
    try {
      const res = await searchUserByIdentifier(q);
      const list = res?.users || [];
      if (list.length === 0) {
        setDirectInviteError("No registered students found with that name or email.");
      }
      setDirectInviteResults(list);
    } catch (err) {
      setDirectInviteError(err?.message || "Search failed.");
    } finally {
      setIsSearchingDirect(false);
    }
  };

  const handleDirectInvite = async (targetUser) => {
    if (!manageGroup || !targetUser || invitingUserId) return;

    if (manageMembers.length >= Number(manageGroup.maxMembers || 5)) {
      setAlert({
        type: "error",
        title: "Group Full",
        message: "This study group has reached its maximum capacity.",
      });
      return;
    }

    try {
      setInvitingUserId(String(targetUser._id));
      const res = await inviteStudyGroupMember(manageGroup.id, {
        targetUserId: targetUser._id,
        email: targetUser.email,
      });

      const newMember = res?.data || {
        userId: targetUser._id,
        firstName: targetUser.firstName || "",
        lastName: targetUser.lastName || "",
        email: targetUser.email || "",
        avatar: targetUser.avatar || "",
        joinedAt: new Date(),
        isOwner: false,
      };

      setManageMembers((prev) => [...prev, newMember]);
      setGroups((prev) =>
        prev.map((g) =>
          g.id === manageGroup.id ? { ...g, members: Number(g.members || 0) + 1 } : g
        )
      );
      setAlert({
        type: "success",
        title: "Member Invited!",
        message: `${targetUser.firstName || "Student"} was successfully added to the group.`,
      });
    } catch (err) {
      setAlert({
        type: "error",
        title: "Invite Failed",
        message: err?.response?.data?.message || err?.message || "Failed to invite member.",
      });
    } finally {
      setInvitingUserId("");
    }
  };

  const handleGenerateManageCode = async () => {
    if (!manageGroup?.id || isGeneratingManageCode) return;
    try {
      setIsGeneratingManageCode(true);
      const res = await generateStudyGroupCode(manageGroup.id);
      const nextCode = res?.inviteCode || "";
      setManageInviteCode(nextCode);
      setGroups((prev) =>
        prev.map((g) =>
          g.id === manageGroup.id ? { ...g, inviteCode: nextCode, inviteCodeActive: true } : g
        )
      );
      setAlert({
        type: "success",
        title: "New Code Generated",
        message: "New invite code is active. Previous code has been invalidated.",
      });
    } catch (error) {
      setAlert({
        type: "error",
        title: "Generation Failed",
        message: error?.response?.data?.message || "Unable to generate code.",
      });
    } finally {
      setIsGeneratingManageCode(false);
    }
  };

  const handleQuickCopyCode = async (group, e) => {
    if (e) e.stopPropagation();
    const code = String(group?.inviteCode || "").trim();
    if (!code) {
      setAlert({
        type: "info",
        title: "No Active Code",
        message: "This group does not have an active invite code. Use Manage Group to generate one.",
      });
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      }
      setAlert({
        type: "success",
        title: "Code Copied!",
        message: `Invite code for "${group.name}" copied to clipboard.`,
      });
    } catch {
      setAlert({
        type: "info",
        title: "Group Code",
        message: `Invite code: ${code}`,
      });
    }
    setOpenMenuId(null);
  };

  const handleRemoveMember = async () => {
    if (!removeMemberModal.member || !manageGroup?.id) return;
    const member = removeMemberModal.member;
    try {
      setIsRemovingMemberId(String(member.userId));
      await removeStudyGroupMember(manageGroup.id, member.userId);
      setManageMembers((prev) => prev.filter((m) => String(m.userId) !== String(member.userId)));
      setRemoveMemberModal({ open: false, member: null });
      setGroups((prev) =>
        prev.map((g) =>
          g.id === manageGroup.id
            ? { ...g, members: Math.max(1, Number(g.members || 1) - 1) }
            : g
        )
      );
      setAlert({
        type: "success",
        title: "Member Removed",
        message: `${member.firstName || "Member"} was successfully removed from the group.`,
      });
    } catch (error) {
      setAlert({
        type: "error",
        title: "Remove Failed",
        message: error?.response?.data?.message || "Unable to remove member.",
      });
    } finally {
      setIsRemovingMemberId("");
    }
  };

  const [isBatchRemovingMembers, setIsBatchRemovingMembers] = useState(false);

  const handleBatchRemoveMembers = (memberIds, onDone) => {
    if (!manageGroup?.id || !Array.isArray(memberIds) || memberIds.length === 0) return;

    const count = memberIds.length;
    Swal.fire({
      title: `Remove ${count} Member${count > 1 ? "s" : ""}?`,
      text: `Are you sure you want to remove ${count} selected member${count > 1 ? "s" : ""} from "${manageGroup.name}"? They will lose access to group files and discussions.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#94A3B8",
      confirmButtonText: `Yes, remove ${count > 1 ? `all ${count}` : "member"}!`,
      cancelButtonText: "Cancel",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          setIsBatchRemovingMembers(true);
          await removeStudyGroupMembersBatch(manageGroup.id, memberIds);

          const idSet = new Set(memberIds.map(String));
          setManageMembers((prev) => prev.filter((m) => !idSet.has(String(m.userId))));

          setGroups((prev) =>
            prev.map((g) =>
              g.id === manageGroup.id
                ? { ...g, members: Math.max(1, Number(g.members || 1) - count) }
                : g
            )
          );

          if (typeof onDone === "function") onDone();

          setAlert({
            type: "success",
            title: "Members Removed",
            message: `Successfully removed ${count} member${count > 1 ? "s" : ""} from the group.`,
          });
        } catch (error) {
          setAlert({
            type: "error",
            title: "Remove Failed",
            message: error?.response?.data?.message || "Failed to remove members.",
          });
        } finally {
          setIsBatchRemovingMembers(false);
        }
      }
    });
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await acceptStudyGroupRequest(manageGroup.id, requestId);
      const remainingRequests = joinRequests.filter((r) => String(r._id) !== String(requestId));
      setJoinRequests(remainingRequests);

      const data = await getStudyGroupMembers(manageGroup.id);
      setManageMembers(Array.isArray(data?.members) ? data.members : []);

      const nextPendingCount = remainingRequests.length;
      let totalPending = 0;
      setGroups((prev) => {
        const next = prev.map((g) =>
          String(g.id) === String(manageGroup.id)
            ? {
                ...g,
                members: Number(g.members || 0) + 1,
                pendingRequestsCount: nextPendingCount,
              }
            : g
        );
        totalPending = next.reduce(
          (sum, g) => (!g.isShared ? sum + Number(g.pendingRequestsCount || 0) : sum),
          0
        );
        queryCache.set("study_groups_mapped", next, 5 * 60 * 1000);
        return next;
      });

      window.dispatchEvent(
        new CustomEvent("study_group_requests_changed", {
          detail: {
            groupId: String(manageGroup.id),
            pendingRequestsCount: nextPendingCount,
            totalPendingRequests: totalPending,
          },
        })
      );

      setAlert({
        type: "success",
        title: "Request Approved",
        message: "New member joined the study group!",
      });
    } catch (err) {
      setAlert({
        type: "error",
        title: "Action Failed",
        message: err?.response?.data?.message || "Failed to accept request.",
      });
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      await declineStudyGroupRequest(manageGroup.id, requestId);
      const remainingRequests = joinRequests.filter((r) => String(r._id) !== String(requestId));
      setJoinRequests(remainingRequests);

      const nextPendingCount = remainingRequests.length;
      let totalPending = 0;
      setGroups((prev) => {
        const next = prev.map((g) =>
          String(g.id) === String(manageGroup.id)
            ? {
                ...g,
                pendingRequestsCount: nextPendingCount,
              }
            : g
        );
        totalPending = next.reduce(
          (sum, g) => (!g.isShared ? sum + Number(g.pendingRequestsCount || 0) : sum),
          0
        );
        queryCache.set("study_groups_mapped", next, 5 * 60 * 1000);
        return next;
      });

      window.dispatchEvent(
        new CustomEvent("study_group_requests_changed", {
          detail: {
            groupId: String(manageGroup.id),
            pendingRequestsCount: nextPendingCount,
            totalPendingRequests: totalPending,
          },
        })
      );

      setAlert({
        type: "info",
        title: "Request Declined",
        message: "The join request was declined.",
      });
    } catch (err) {
      setAlert({
        type: "error",
        title: "Action Failed",
        message: err?.response?.data?.message || "Failed to decline request.",
      });
    }
  };

  const handleEditGroupClick = (group) => {
    setEditingGroupId(group.id);
    setEditGroupData({
      name: group.name || "",
      subject: group.subject || "",
      maxMembers: group.maxMembers || 5,
      color: group.color || "blue",
    });
    setIsEditModalOpen(true);
    setOpenMenuId(null);
  };

  const handleDeleteGroupClick = (group) => {
    setConfirmModal({
      open: true,
      type: "delete",
      groupId: group.id,
      title: "Delete Study Group?",
      message: `Are you sure you want to permanently delete "${group.name}"? All shared files, messages, and discussions will be removed.`,
      confirmLabel: "Delete Group",
      isProcessing: false,
    });
    setOpenMenuId(null);
  };

  const handleLeaveGroupClick = (group) => {
    setConfirmModal({
      open: true,
      type: "leave",
      groupId: group.id,
      title: "Leave Study Group?",
      message: `Are you sure you want to leave "${group.name}"? You will lose access to discussions unless invited back.`,
      confirmLabel: "Leave Group",
      isProcessing: false,
    });
    setOpenMenuId(null);
  };

  const handleConfirmAction = async () => {
    if (!confirmModal.groupId) return;
    try {
      setConfirmModal((prev) => ({ ...prev, isProcessing: true }));
      if (confirmModal.type === "delete") {
        await deleteStudyGroup(confirmModal.groupId);
        setGroups((prev) => prev.filter((g) => g.id !== confirmModal.groupId));
        setAlert({
          type: "success",
          title: "Group Deleted",
          message: "The study group was successfully deleted.",
        });
      } else if (confirmModal.type === "leave") {
        await leaveStudyGroup(confirmModal.groupId);
        setGroups((prev) => prev.filter((g) => g.id !== confirmModal.groupId));
        setAlert({
          type: "success",
          title: "Left Group",
          message: "You have left the study group.",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        title: "Action Failed",
        message: error?.response?.data?.message || "Unable to complete action.",
      });
    } finally {
      setConfirmModal({
        open: false,
        type: "",
        groupId: null,
        title: "",
        message: "",
        confirmLabel: "Confirm",
        isProcessing: false,
      });
    }
  };

  return (
    <div className="bg-[#F8F9FA] text-slate-800 flex h-screen overflow-hidden font-sans antialiased">
      {alert && <SwiftAlert {...alert} onClose={() => setAlert(null)} />}

      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-[1400px] mx-auto w-full space-y-7">
            
            {/* HERO BANNER WITH METRIC HIGHLIGHTS */}
            <StudyGroupHero
              totals={totals}
              onEnterCode={() => setIsEnterCodeModalOpen(true)}
              onCreateGroup={() => setIsCreateModalOpen(true)}
              onOpenStudyBuddies={() => setIsStudyBuddiesModalOpen(true)}
              onSelectTab={(tab) => setActiveTab(tab)}
            />

            {/* SEARCH, FILTER & TAB CONTROLS */}
            <section className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
              
              {/* Filter Tabs */}
              <div className="flex items-center gap-1.5 p-1 bg-gray-50 rounded-xl border border-gray-100 shrink-0 overflow-x-auto">
                <button
                  onClick={() => setActiveTab("all")}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                    activeTab === "all"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  All Groups ({totals.totalGroups})
                </button>
                <button
                  onClick={() => setActiveTab("owned")}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                    activeTab === "owned"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  <span>Created by Me ({totals.ownedCount})</span>
                  {totals.totalPendingRequests > 0 && (
                    <span className="w-2 h-2 rounded-full bg-red-500 ring-2 ring-white animate-pulse shrink-0"></span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab("joined")}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                    activeTab === "joined"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  Joined Groups ({totals.joinedCount})
                </button>
              </div>

              {/* Search, Subject Filter, Sort & Refresh */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1 lg:justify-end">
                
                {/* Search Bar */}
                <div className="relative flex-1 sm:max-w-xs">
                  <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search by name, subject, or code..."
                    className="w-full pl-9 pr-8 py-2 rounded-xl text-xs bg-gray-50 border border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition font-medium text-gray-800"
                  />
                  {searchQuery && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                      aria-label="Clear search"
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  )}
                </div>

                {/* Subject Selector */}
                {availableSubjects.length > 0 && (
                  <div className="relative">
                    <select
                      value={selectedSubject}
                      onChange={(e) => setSelectedSubject(e.target.value)}
                      className="w-full sm:w-auto px-3 py-2 pr-8 rounded-xl text-xs font-semibold bg-gray-50 border border-gray-200 text-gray-700 outline-none focus:border-blue-500 transition appearance-none cursor-pointer"
                    >
                      <option value="all">All Subjects</option>
                      {availableSubjects.map((sub) => (
                        <option key={sub} value={sub}>
                          {sub}
                        </option>
                      ))}
                    </select>
                    <i className="fa-solid fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none"></i>
                  </div>
                )}

                {/* Sort Selector */}
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2 pr-8 rounded-xl text-xs font-semibold bg-gray-50 border border-gray-200 text-gray-700 outline-none focus:border-blue-500 transition appearance-none cursor-pointer"
                  >
                    <option value="recent">Recently Active</option>
                    <option value="name">Name (A-Z)</option>
                    <option value="members">Most Members</option>
                  </select>
                  <i className="fa-solid fa-arrow-down-short-wide absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none"></i>
                </div>

                {/* Refresh Button */}
                <button
                  type="button"
                  onClick={() => loadGroups(true)}
                  disabled={isLoadingGroups}
                  title="Refresh groups"
                  className="px-3 py-2 rounded-xl text-xs font-semibold bg-gray-50 hover:bg-gray-100 text-gray-600 border border-gray-200 transition flex items-center justify-center gap-1.5"
                >
                  <i className={`fa-solid fa-arrows-rotate text-[11px] ${isLoadingGroups ? "fa-spin text-blue-600" : ""}`}></i>
                  <span className="hidden sm:inline">Refresh</span>
                </button>

              </div>
            </section>

            {/* STUDY GROUPS GRID */}
            <section>
              <div className="flex items-center justify-between mb-4 px-1">
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <span>Available Groups</span>
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold">
                    {filteredGroups.length}
                  </span>
                </h2>

                {(searchQuery || selectedSubject !== "all" || activeTab !== "all") && (
                  <button
                    onClick={() => {
                      handleClearSearch();
                      setSelectedSubject("all");
                      setActiveTab("all");
                    }}
                    className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition"
                  >
                    <i className="fa-solid fa-rotate-left text-[10px]"></i>
                    <span>Reset Filters</span>
                  </button>
                )}
              </div>

              {/* SKELETON LOADING STATE */}
              {isLoadingGroups ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {[1, 2, 3, 4].map((n) => (
                    <div
                      key={n}
                      className="bg-white rounded-3xl p-5 border border-gray-100 shadow-sm animate-pulse flex flex-col h-[280px]"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 rounded-xl bg-gray-200"></div>
                        <div className="w-16 h-5 rounded-full bg-gray-200"></div>
                      </div>
                      <div className="w-3/4 h-5 bg-gray-200 rounded mb-2"></div>
                      <div className="w-1/2 h-3.5 bg-gray-200 rounded mb-6"></div>
                      <div className="mt-auto space-y-2">
                        <div className="w-full h-2 bg-gray-200 rounded"></div>
                        <div className="w-full h-10 bg-gray-200 rounded-xl"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredGroups.length === 0 ? (
                /* EMPTY STATE */
                <div className="bg-white rounded-3xl border border-gray-100 p-10 md:p-14 text-center max-w-xl mx-auto shadow-sm">
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center text-2xl mx-auto mb-4">
                    <i className="fa-solid fa-user-group"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {searchQuery || selectedSubject !== "all" || activeTab !== "all"
                      ? "No study groups match your filters"
                      : "No study groups yet"}
                  </h3>
                  <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                    {searchQuery || selectedSubject !== "all" || activeTab !== "all"
                      ? "Try searching with a different term or resetting your filter criteria."
                      : "Start your first study group to share verified reviewers, or join an existing peer circle with an invite code."}
                  </p>

                  <div className="flex flex-wrap items-center justify-center gap-3">
                    {searchQuery || selectedSubject !== "all" || activeTab !== "all" ? (
                      <button
                        onClick={() => {
                          handleClearSearch();
                          setSelectedSubject("all");
                          setActiveTab("all");
                        }}
                        className="px-5 py-2.5 rounded-xl font-bold text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
                      >
                        Clear Filters
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => setIsEnterCodeModalOpen(true)}
                          className="px-4 py-2.5 rounded-xl font-bold text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
                        >
                          <i className="fa-solid fa-key mr-2 text-xs"></i> Enter Code
                        </button>
                        <button
                          onClick={() => setIsCreateModalOpen(true)}
                          className="px-5 py-2.5 rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-700 text-white transition shadow-sm"
                        >
                          <i className="fa-solid fa-plus mr-2 text-xs"></i> Create Group
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredGroups.map((group) => (
                    <StudyGroupCard
                      key={group.id}
                      group={group}
                      openMenuId={openMenuId}
                      setOpenMenuId={setOpenMenuId}
                      menuRef={menuRef}
                      onManageGroup={handleManageGroup}
                      onEditGroup={handleEditGroupClick}
                      onQuickCopyCode={handleQuickCopyCode}
                      onDeleteGroup={handleDeleteGroupClick}
                      onLeaveGroup={handleLeaveGroupClick}
                      onEnterHub={(groupId) => navigate(`/study-group/${groupId}/hub`)}
                    />
                  ))}

                  {/* ADD GROUP DASHED CARD */}
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(true)}
                    className="group bg-transparent border-2 border-dashed border-gray-200 hover:border-blue-500 hover:bg-blue-50/40 rounded-3xl flex flex-col items-center justify-center text-gray-400 hover:text-blue-600 transition-all duration-200 cursor-pointer min-h-[280px] p-6 text-center"
                  >
                    <div className="w-12 h-12 rounded-2xl border border-current flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <i className="fa-solid fa-plus text-base"></i>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider mb-1">
                      Create Study Group
                    </span>
                    <span className="text-[11px] text-gray-400 max-w-[180px]">
                      Build a peer circle with customized capacity and topic
                    </span>
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      {/* ========================================================================= */}
      {/* CREATE GROUP MODAL */}
      {/* ========================================================================= */}
      <CreateGroupModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        groupData={groupData}
        setGroupData={setGroupData}
        onSubmit={handleCreateGroup}
        isSubmitting={isSubmittingCreate}
      />

      {/* ========================================================================= */}
      {/* EDIT GROUP MODAL */}
      {/* ========================================================================= */}
      <EditGroupModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingGroupId(null);
        }}
        editGroupData={editGroupData}
        setEditGroupData={setEditGroupData}
        onSubmit={handleUpdateGroup}
        isSubmitting={isSubmittingEdit}
      />

      {/* ========================================================================= */}
      {/* ENTER CODE MODAL */}
      {/* ========================================================================= */}
      <EnterCodeModal
        isOpen={isEnterCodeModalOpen}
        onClose={() => {
          setIsEnterCodeModalOpen(false);
          setInviteCode("");
        }}
        inviteCode={inviteCode}
        setInviteCode={setInviteCode}
        onPasteCode={handlePasteCode}
        onJoinWithCode={handleJoinWithCode}
        isJoiningCode={isJoiningCode}
      />

      {/* ========================================================================= */}
      {/* MANAGE GROUP MODAL */}
      {/* ========================================================================= */}
      <ManageGroupModal
        isOpen={isManageModalOpen}
        onClose={() => {
          setIsManageModalOpen(false);
          setManageGroup(null);
          setManageMembers([]);
          setManageInviteCode("");
        }}
        manageGroup={manageGroup}
        manageTab={manageTab}
        setManageTab={setManageTab}
        manageMembers={manageMembers}
        isLoadingMembers={isLoadingMembers}
        isRemovingMemberId={isRemovingMemberId}
        onOpenRemoveMemberModal={(member) => setRemoveMemberModal({ open: true, member })}
        avatarFallbackMap={avatarFallbackMap}
        setAvatarFallbackMap={setAvatarFallbackMap}
        joinRequests={joinRequests}
        isLoadingRequests={isLoadingRequests}
        onAcceptRequest={handleAcceptRequest}
        onDeclineRequest={handleDeclineRequest}
        directInviteQuery={directInviteQuery}
        setDirectInviteQuery={setDirectInviteQuery}
        directInviteResults={directInviteResults}
        isSearchingDirect={isSearchingDirect}
        directInviteError={directInviteError}
        setDirectInviteError={setDirectInviteError}
        invitingUserId={invitingUserId}
        onSearchDirectUser={handleSearchDirectUser}
        onDirectInvite={handleDirectInvite}
        manageInviteCode={manageInviteCode}
        isGeneratingManageCode={isGeneratingManageCode}
        onGenerateManageCode={handleGenerateManageCode}
        onBatchRemoveMembers={handleBatchRemoveMembers}
        isBatchRemovingMembers={isBatchRemovingMembers}
        onCopyInviteCode={async () => {
          if (!manageInviteCode) return;
          if (navigator?.clipboard?.writeText) {
            try {
              await navigator.clipboard.writeText(manageInviteCode);
              setAlert({
                type: "success",
                title: "Code Copied!",
                message: "Invite code copied to your clipboard.",
              });
            } catch {
              // no-op
            }
          }
        }}
      />

      {/* ========================================================================= */}
      {/* CONFIRM ACTION MODAL */}
      {/* ========================================================================= */}
      <ConfirmActionModal
        confirmModal={confirmModal}
        onClose={() =>
          setConfirmModal({
            open: false,
            type: "",
            groupId: null,
            title: "",
            message: "",
            confirmLabel: "Confirm",
            isProcessing: false,
          })
        }
        onConfirm={handleConfirmAction}
      />

      {/* ========================================================================= */}
      {/* REMOVE MEMBER CONFIRMATION MODAL */}
      {/* ========================================================================= */}
      <RemoveMemberModal
        removeMemberModal={removeMemberModal}
        onClose={() => setRemoveMemberModal({ open: false, member: null })}
        onConfirm={async (member) => {
          setRemoveMemberModal({ open: false, member: null });
          if (member) await handleRemoveMember(member);
        }}
      />

      {/* ========================================================================= */}
      {/* STUDY BUDDIES MODAL (ALL MEMBERS ACROSS ALL GROUPS) */}
      {/* ========================================================================= */}
      <StudyBuddiesModal
        isOpen={isStudyBuddiesModalOpen}
        onClose={() => setIsStudyBuddiesModalOpen(false)}
      />
    </div>
  );
};

export default StudyGroup;
